import { useCallback, useState, type ReactElement } from "react";
import { UnsavedChangesDialog } from "@/components/common/unsaved-changes-dialog";

export interface UseCloseGuardOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDirty: boolean;
  isSubmitting?: boolean;
  onDiscard?: () => void;
}

export interface UseCloseGuardResult {
  onOpenChange: (open: boolean) => void;
  dialog: ReactElement;
}

export function useCloseGuard({
  open,
  onOpenChange,
  isDirty,
  isSubmitting = false,
  onDiscard,
}: UseCloseGuardOptions): UseCloseGuardResult {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const guarded = useCallback(
    (nextOpen: boolean) => {
      // A submit in flight owns the dialog. ESC, an outside click and the corner X all arrive
      // here, and letting one of them win would hide a request the user can no longer see the
      // result of — while leaving that result to land on a dialog they may have reopened for a
      // different record in the meantime. The submitting flow closes itself from its own success
      // handler, which sets `open` in the parent and never passes through this guard.
      if (open && !nextOpen && isSubmitting) {
        return;
      }

      if (open && !nextOpen && isDirty) {
        setConfirmOpen(true);
        return;
      }

      onOpenChange(nextOpen);
    },
    [open, isDirty, isSubmitting, onOpenChange],
  );

  const handleDiscard = useCallback(() => {
    setConfirmOpen(false);
    onDiscard?.();
    onOpenChange(false);
  }, [onDiscard, onOpenChange]);

  const handleCancel = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const dialog = (
    <UnsavedChangesDialog
      open={confirmOpen}
      onDiscard={handleDiscard}
      onCancel={handleCancel}
    />
  );

  return { onOpenChange: guarded, dialog };
}
